import type { FormVersion, Rule, RuleAction, RuleCondition, RuleOperator } from "../state/formVersion";
import { RuleActionKey, useFormVersionDispatch, useFormVersionState } from "../state/formVersion";
import type { SetStoreFunction } from "solid-js/store";
import { produce } from "solid-js/store";
import { initialized } from "../utils/initializedUtil";
import RuleId from "../fields/RuleId";
import type SubformId from "../state/SubformId";
import { cloneDeep, isNil } from "lodash-es";
import type FieldId from "../fields/FieldId";
import { Scope } from "../types";

let setFormVersionStore: SetStoreFunction<FormVersion> | null = null;
let formVersionStore: FormVersion | null = null;

const getFormVersionStore = () => initialized(formVersionStore, "formVersionStore");
const getSetFormVersionStore = () => initialized(setFormVersionStore, "setFormVersionStore");

const init = () => {
  setFormVersionStore = useFormVersionDispatch();
  formVersionStore = useFormVersionState();
};

export type RuleServiceType = {
  getAll: () => Rule[];
  get: (ruleId: RuleId) => Rule | undefined;
  changeName: (id: RuleId, name: string) => void;
  changeOperator: (id: RuleId, operator: RuleOperator) => void;
  changeConditions: (id: RuleId, conditions: Partial<RuleCondition>[]) => void;
  changeActions: (id: RuleId, conditions: Partial<RuleAction>[]) => void;
  move: (fromIndex: number, toIndex: number) => void;
  addToEnd: (rule: Omit<Rule, "scope">) => void;
  addToEndWithScope: (rule: Rule) => void;
  delete: (id: RuleId) => void;
  duplicate: (ruleId: RuleId, references?: Map<string, FieldId>, duplicateToSubform?: SubformId) => void;
};

const create = (ruleScopingFilter: (rule: Rule) => boolean, subformId?: SubformId): RuleServiceType => {
  const getScopedRules = () => getFormVersionStore().rules.filter(ruleScopingFilter);
  const getScopedRule = (ruleId: RuleId): Rule | undefined => {
    const rule = getScopedRules().find((rule) => ruleId.equals(rule.id));
    return isNil(rule) ? undefined : rule;
  };

  return {
    getAll: getScopedRules,
    get: getScopedRule,
    changeName: (id: RuleId, name: string) => {
      getSetFormVersionStore()("rules", (rule) => id.equals(rule.id), "name", name);
    },
    changeOperator: (id: RuleId, operator: RuleOperator) => {
      getSetFormVersionStore()("rules", (rule) => id.equals(rule.id), "operator", operator);
    },
    changeConditions: (id: RuleId, conditions: Partial<RuleCondition>[]) => {
      getSetFormVersionStore()("rules", (rule) => id.equals(rule.id), "conditions", conditions as RuleCondition[]);
    },
    changeActions: (id: RuleId, actions: Partial<RuleAction>[]) => {
      getSetFormVersionStore()("rules", (rule) => id.equals(rule.id), "actions", actions as RuleAction[]);
    },
    move: (fromIndex: number, toIndex: number) => {
      getSetFormVersionStore()("rules", (prev) => {
        const scoped = prev.filter(ruleScopingFilter);
        const reordered = [...scoped];
        const [item] = reordered.splice(fromIndex, 1);
        reordered.splice(toIndex, 0, item);

        // Write the reordered scoped rules back into their original slots in the
        // flat array; rules from other scopes keep their positions.
        let next = 0;
        return prev.map((rule) => (ruleScopingFilter(rule) ? reordered[next++] : rule));
      });
    },
    addToEnd: (rule: Omit<Rule, "scope">) =>
      getSetFormVersionStore()(
        produce((state) => {
          state.rules.push({ ...rule, scope: subformId ?? Scope.ROOT });
        }),
      ),
    addToEndWithScope: (rule: Rule) => getSetFormVersionStore()("rules", (prev) => [...prev, rule]),
    delete: (id: RuleId) => {
      getSetFormVersionStore()("rules", (prev) => prev.filter((rule) => !id.equals(rule.id)));
    },
    duplicate: (ruleId: RuleId, references?: Map<string, FieldId>, duplicateToSubform?: SubformId) => {
      getSetFormVersionStore()("rules", (prev: Rule[]) => {
        const fieldIndex = prev.findIndex((rule) => ruleId.equals(rule.id));
        if (fieldIndex === -1) {
          return prev;
        }

        const prevRule = prev[fieldIndex];
        const clonedRule: Rule = {
          ...cloneDeep(prevRule),
          id: RuleId.random(),
        };

        if (!isNil(duplicateToSubform) && !isNil(references)) {
          clonedRule.scope = duplicateToSubform;
          clonedRule.conditions = prevRule.conditions.map((condition) => {
            if (condition.type === "FIELD_DATA_SOURCE" || condition.type === "FIELD") {
              // If reference isn't found, like would be the case for a parent field, clear the reference
              return { ...condition, fieldId: references.get(condition.fieldId.toString()) } as RuleCondition;
            }
            return condition;
          });
          clonedRule.actions = prevRule.actions.map((action) => {
            if (action.key === RuleActionKey.EMPTY) {
              return action;
            } else if (action.key === RuleActionKey.REFERENCE) {
              return {
                ...action,
                fieldId: references.get(action.fieldId.toString()) ?? action.fieldId,
                value: !isNil(action.value) ? references.get(action.value.toString()) : action.value,
              };
            } else {
              return {
                ...action,
                fieldId: references.get(action.fieldId.toString()) ?? action.fieldId,
              };
            }
          });
        }

        return [...prev.slice(0, fieldIndex + 1), clonedRule, ...prev.slice(fieldIndex + 1)];
      });
    },
  };
};

const unscopedRuleService = create(() => true);

export default {
  init,
  create,
  getAll: unscopedRuleService.getAll,
  get: unscopedRuleService.get,
  changeOperator: unscopedRuleService.changeOperator,
  changeName: unscopedRuleService.changeName,
  changeActions: unscopedRuleService.changeActions,
  changeConditions: unscopedRuleService.changeConditions,
  move: unscopedRuleService.move,
  addToEnd: unscopedRuleService.addToEnd,
  delete: unscopedRuleService.delete,
  duplicate: unscopedRuleService.duplicate,
};
